import {useCallback} from 'react';

import {useMCPToolMappingOptional} from 'llm/mcp-client-library/MCPToolMappingContext';
import {error as logError} from 'util/Logging';

import {TOOL_DESCRIPTIONS, DeepResearchToolType, isMCPTool, getBasicToolDisplayName} from './types';

/**
 * React hook for getting user-friendly tool display names.
 *
 * For MCP tools, this returns friendly names like "Server Name: Tool Title"
 * instead of the raw "serverApiId__toolName" format. Tool titles are fetched
 * from the cached MCP server metadata.
 *
 * For built-in tools (DeepResearchToolType enum values), returns the configured description.
 *
 * Must be used within an MCPToolMappingProvider for best results with MCP tools.
 * Falls back to basic display names (without title) if used outside the provider
 * or if titles haven't been cached yet.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const getToolDisplayName = useToolDisplayName();
 *   return <div>{getToolDisplayName('mcpconf_123__list_repos')}</div>;
 *   // Renders: "GitHub: List Repositories" (if title is cached)
 *   // Falls back to: "GitHub: list_repos" (if no title available)
 *   // Falls back to: "mcpconf_123: list_repos" (if no server mapping)
 * }
 * ```
 */
export function useToolDisplayName(): (toolName: DeepResearchToolType | string) => string {
  const mcpMapping = useMCPToolMappingOptional();

  return useCallback(
    (toolName: DeepResearchToolType | string): string => {
      if (isMCPTool(toolName)) {
        // For MCP tools, use the mapping provider if available, otherwise fall back to basic format
        return mcpMapping?.getFullToolDisplayName(toolName) ?? getBasicToolDisplayName(toolName);
      }

      if (TOOL_DESCRIPTIONS.hasOwnProperty(toolName)) {
        return TOOL_DESCRIPTIONS[toolName as DeepResearchToolType];
      }

      // Log unknown tool names to Sentry for monitoring
      logError(`[llm] Unknown tool name: ${toolName}`);
      return toolName;
    },
    [mcpMapping]
  );
}
